import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Clock, CalendarX, Upload } from "lucide-react";
import { uploadCert } from "@/components/SickCert";
import { addDays } from "date-fns";
import { Calendar } from "@/components/ui/calendar";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const toKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fromKey = (k) => { const [y, m, dd] = k.split("-").map(Number); return new Date(y, m - 1, dd); };

export function AvailabilityEditor({ kind, id, initial, initialBlocked = [], canEdit = true, onSaved }) {
  const merge = () => {
    const map = {};
    (initial || []).forEach((x) => { map[x.day] = x; });
    return DAYS.map((d) => (map[d]
      ? { day: d, available: !!map[d].available, start: map[d].start || "09:00", end: map[d].end || "17:00" }
      : { day: d, available: false, start: "09:00", end: "17:00" }));
  };
  const [rows, setRows] = useState(merge());
  const [saving, setSaving] = useState(false);
  const [blocked, setBlocked] = useState((initialBlocked || []).map(fromKey));
  const [savingBlocked, setSavingBlocked] = useState(false);
  const [sick, setSick] = useState(false);
  const [cert, setCert] = useState(null);
  const upd = (i, k, v) => setRows((r) => r.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/${kind === "staff" ? "staff" : "clients"}/${id}/availability`, { availability: rows });
      toast.success("Weekly availability saved"); onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const saveBlocked = async () => {
    setSavingBlocked(true);
    try {
      const dates = [...new Set(blocked.map(toKey))].sort();
      await api.put(`/staff/${id}/blocked-dates`, { dates });
      if (sick) {
        const fresh = dates.filter((k) => !(initialBlocked || []).includes(k));
        const runs = [];
        fresh.forEach((k) => {
          const last = runs[runs.length - 1];
          const next = last && toKey(addDays(fromKey(last[1]), 1));
          if (last && next === k) { last[1] = k; } else { runs.push([k, k]); }
        });
        for (const [i, [start, end]] of runs.entries()) {
          const { data } = await api.post("/leave", { subject_type: "staff", subject_id: id, kind: "sickness", start_date: start, end_date: end, reason: "Off sick (blocked days)" });
          if (i === 0 && cert && data?.id) { await uploadCert(data.id, cert); }
        }
        if (runs.length) { toast.success(`Sickness recorded${cert ? " with certificate" : ""} — the office has been told`); }
        setSick(false); setCert(null);
      }
      toast.success(dates.length ? `${dates.length} day(s) blocked out` : "Blocked dates cleared");
      onSaved && onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSavingBlocked(false); }
  };
  const inp = "rounded-lg border border-border bg-white px-2 py-1 text-xs outline-none focus:border-brand";
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return (
    <div data-testid="availability-editor" className="space-y-5">
      <div>
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><Clock size={13} /> Weekly availability</p>
        <div className="mt-2 space-y-1">
          {rows.map((r, i) => (
            <div key={r.day} className="flex items-center gap-2 text-sm">
              <label className="flex w-24 items-center gap-2">
                <input type="checkbox" className="h-4 w-4 accent-brand" checked={r.available} disabled={!canEdit} onChange={(e) => upd(i, "available", e.target.checked)} data-testid={`avail-${r.day}`} />
                <span className={r.available ? "font-medium text-ink" : "text-ink-muted"}>{r.day}</span>
              </label>
              {r.available ? (
                <>
                  <input type="time" className={inp} value={r.start} disabled={!canEdit} onChange={(e) => upd(i, "start", e.target.value)} />
                  <span className="text-ink-muted">–</span>
                  <input type="time" className={inp} value={r.end} disabled={!canEdit} onChange={(e) => upd(i, "end", e.target.value)} />
                </>
              ) : <span className="text-xs text-ink-muted">Unavailable</span>}
            </div>
          ))}
        </div>
        {canEdit && <button onClick={save} disabled={saving} data-testid="save-availability-button" className="mt-2 rounded-lg bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-60">{saving ? "Saving…" : "Save weekly availability"}</button>}
      </div>

      {kind === "staff" && (
        <div className="border-t border-border pt-4">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-ink-muted"><CalendarX size={13} /> Block out specific days</p>
          <p className="mt-1 text-xs text-ink-muted">Tap any days you can't work (holidays, appointments). Blocked days can't be rostered.</p>
          <div className="mt-2 flex justify-center rounded-lg border border-border">
            <Calendar
              mode="multiple"
              selected={blocked}
              onSelect={(d) => canEdit && setBlocked(d || [])}
              disabled={{ before: today }}
              data-testid="blocked-dates-calendar"
            />
          </div>
          {blocked.length > 0 && (
            <p className="mt-2 text-xs text-ink-soft" data-testid="blocked-dates-count">{blocked.length} day(s) blocked out.</p>
          )}
          {canEdit && blocked.length > 0 && (
            <div className="mt-2 space-y-2 rounded-lg border border-coral/30 bg-coral-light/30 p-3" data-testid="blocked-sick-box">
              <label className="flex items-center gap-2 text-sm font-medium text-ink"><input type="checkbox" checked={sick} onChange={(e) => setSick(e.target.checked)} data-testid="blocked-sick-checkbox" className="h-4 w-4 accent-coral" /> I'm off sick on these days</label>
              {sick && (
                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border bg-white px-3 py-2 text-xs text-ink-soft hover:bg-secondary">
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setCert(e.target.files[0] || null)} data-testid="blocked-sick-cert-input" />
                  <Upload size={14} /> {cert ? cert.name : "Attach sick certificate or fit note (optional — you can add it later on the Leave page)"}
                </label>
              )}
            </div>
          )}
          {canEdit && <button onClick={saveBlocked} disabled={savingBlocked} data-testid="save-blocked-dates-button" className="mt-2 rounded-lg bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-60">{savingBlocked ? "Saving…" : "Save blocked days"}</button>}
        </div>
      )}
    </div>
  );
}
